import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users,
  PaperPlaneTilt,
  Trash,
  Copy,
  ChartPie,
  ShoppingBag,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminWaitlistPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [expiresDays, setExpiresDays] = useState(14);
  const [inviteResult, setInviteResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [waitRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/marketing/waitlist`, { headers: getHeaders() }),
        axios.get(`${API}/api/marketing/admin/stats`, { headers: getHeaders() }),
      ]);
      setItems(waitRes.data.items || []);
      setStats(waitRes.data.stats);
      setPlatformStats(statsRes.data);
    } catch (err) {
      toast.error('Kunne ikke laste admin-data');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const sendInvite = async () => {
    if (!inviting) return;
    try {
      const { data } = await axios.post(
        `${API}/api/marketing/waitlist/invite`,
        { email: inviting.email, expires_days: expiresDays },
        { headers: getHeaders() }
      );
      setInviteResult(data);
      toast.success('Invite opprettet!');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite feilet');
    }
  };

  const copyInviteLink = () => {
    if (!inviteResult) return;
    const url = `${window.location.origin}/login?invite=${inviteResult.code}`;
    navigator.clipboard.writeText(url);
    toast.success('Lenke kopiert!');
  };

  const removeEntry = async (email) => {
    if (!window.confirm(`Slett ${email} fra ventelisten?`)) return;
    try {
      await axios.delete(`${API}/api/marketing/waitlist/${encodeURIComponent(email)}`, { headers: getHeaders() });
      toast.success('Slettet');
      fetchAll();
    } catch {
      toast.error('Feilet');
    }
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Laster...</div>;

  return (
    <div className="space-y-6" data-testid="admin-waitlist-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-4xl tracking-tight font-extrabold">Admin – Venteliste & Stats</h1>
        <p className="text-sm text-muted-foreground mt-1">Inviter nye beta-brukere og overvåk plattformen.</p>
      </div>

      {/* Platform stats */}
      {platformStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Registrerte brukere', value: platformStats.users, icon: Users },
            { label: 'Aktive tenants', value: platformStats.tenants, icon: ShoppingBag },
            { label: 'På venteliste', value: platformStats.waitlist_waiting, icon: PaperPlaneTilt },
            { label: 'Totalt produkter håndtert', value: platformStats.products_total?.toLocaleString('nb-NO'), icon: ChartPie },
          ].map((s) => (
            <Card key={s.label} className="border border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <s.icon size={28} className="text-primary opacity-70" />
                <div>
                  <p className="font-heading text-2xl font-extrabold">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Waitlist summary */}
      {stats && (
        <div className="flex gap-2 flex-wrap">
          <Badge variant="secondary">Totalt: {stats.total}</Badge>
          <Badge variant="outline" className="border-warning/40">Venter: {stats.waiting}</Badge>
          <Badge variant="outline" className="border-primary/40">Invitert: {stats.invited}</Badge>
          <Badge variant="outline" className="border-success/40">Registrert: {stats.registered}</Badge>
        </div>
      )}

      {/* Waitlist table */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Venteliste ({items.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Ingen på ventelisten enda.</div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((item, i) => (
                <div key={item.email} className="p-4 flex items-center justify-between gap-3" data-testid={`waitlist-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{item.name || item.email}</span>
                      <Badge
                        variant="outline"
                        className={
                          item.status === 'registered' ? 'border-success/40 text-success' :
                          item.status === 'invited' ? 'border-primary/40 text-primary' :
                          'border-warning/40 text-warning'
                        }
                      >
                        {item.status}
                      </Badge>
                      {item.tier_interest && <Badge variant="secondary" className="text-xs">{item.tier_interest}</Badge>}
                      {item.product_count && <Badge variant="outline" className="text-xs">{item.product_count}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.email}{item.shop_url ? ` · ${item.shop_url}` : ''}
                    </p>
                    {item.message && <p className="text-xs text-muted-foreground mt-1 italic">"{item.message}"</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {item.status === 'waiting' && (
                      <Button size="sm" onClick={() => setInviting(item)} className="gap-1" data-testid={`invite-btn-${i}`}>
                        <PaperPlaneTilt size={12} /> Inviter
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => removeEntry(item.email)} className="gap-1 text-destructive">
                      <Trash size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite dialog */}
      <Dialog open={!!inviting} onOpenChange={(o) => { if (!o) { setInviting(null); setInviteResult(null); } }}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Inviter {inviting?.email}</DialogTitle>
          </DialogHeader>
          {!inviteResult ? (
            <div className="space-y-4">
              <div>
                <Label>Utløper etter (dager)</Label>
                <Input type="number" value={expiresDays} onChange={(e) => setExpiresDays(parseInt(e.target.value) || 14)} min={1} max={90} />
              </div>
              <p className="text-xs text-muted-foreground">
                Du får en kopierbar invite-lenke som du sender manuelt til brukeren (e-post, Slack, etc.).
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviting(null)}>Avbryt</Button>
                <Button onClick={sendInvite} data-testid="send-invite-btn">Opprett invite</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <Card className="border-success/40 bg-success/5">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-2">Invite opprettet ✓</p>
                  <p className="text-xs text-muted-foreground mb-3">Send denne lenken til brukeren:</p>
                  <div className="bg-background p-2 rounded border border-border font-mono text-xs break-all">
                    {window.location.origin}/login?invite={inviteResult.code}
                  </div>
                </CardContent>
              </Card>
              <DialogFooter>
                <Button onClick={copyInviteLink} className="gap-2" data-testid="copy-invite-btn">
                  <Copy size={14} /> Kopier lenke
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
